// Project Overview — several RSA projects of one order as ONE dashboard: shared
// map, milepost strips, deficiency summary, multi-deficiency clusters, sites and
// the order PDF. The Safety Dashboard's concept without crash data or
// countermeasures. The server scopes everything to the projects the signed-in
// user is assigned to; all filtering below is client-side over that data.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../Safety/SafetyDashboard.css';
import './ProjectOverview.css';
import { MultiPicker } from '../Safety/CorridorPicker';
import OverviewMap from './OverviewMap';
import OverviewStrips from './OverviewStrips';
import { ClustersTable, SitesTable, SummaryTable } from './OverviewTables';
import { CriteriaDialog, ManagerDialog, PdfDialog, REPORT_NAME } from './OverviewDialogs';
import { fetchCorridors, fetchOverviews, fetchSegment, fetchStations, mapLimit } from './overviewApi';
import { DETAIL_ANALYSES, MAX_DETAIL_ANALYSES } from './StationsLayer';
import {
  ANALYSES, ANALYSIS_COLORS, ANALYSIS_NAMES, ROAD_COLORS, SEVERITIES, clusterSites, dedupeSegments, filterSites, numberSegments,
  overviewStats, positionFn, segmentName, stripBlocks, summarize,
} from './overviewModel';
import { applySeverity, applySeverityColors, loadSeveritySettings, saveSeveritySettings } from './severitySettings';

const LAST_KEY = 'overview:last';
const remember = (id) => { try { localStorage.setItem(LAST_KEY, String(id)); } catch (e) { /* private window */ } };
const recall = () => { try { return Number(localStorage.getItem(LAST_KEY)) || null; } catch (e) { return null; } };

const SectionCard = ({ title, meta, tools, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="safety-section">
      <div className="ov-section-head">
        <button type="button" className="safety-section-head ov-section-toggle" onClick={() => setOpen(!open)} aria-expanded={open}>
          <span className={`safety-section-chevron ${open ? 'open' : ''}`}>▸</span>
          <span className="safety-section-title">{title}</span>
          {meta && <span className="ov-section-meta">{meta}</span>}
        </button>
        {open && tools && <div className="ov-section-tools">{tools}</div>}
      </div>
      {open && <div className="safety-section-body">{children}</div>}
    </div>
  );
};

const Seg = ({ value, onChange, options, label }) => (
  <div className="ov-seg" role="group" aria-label={label}>
    {options.map(([v, text]) => (
      <button key={v} type="button" aria-pressed={value === v} onClick={() => onChange(v)}>{text}</button>
    ))}
  </div>
);

const RowsSelect = ({ id, value, onChange }) => (
  <label className="ov-inline" htmlFor={id}>Rows to show
    <select id={id} className="ov-select" value={value} onChange={(e) => onChange(Number(e.target.value))}>
      <option value={10}>Top 10</option><option value={25}>Top 25</option><option value={50}>Top 50</option>
      <option value={100}>Top 100</option><option value={0}>All</option>
    </select>
  </label>
);

const Tile = ({ label, value, sub, wide, children }) => (
  <div className={`ov-tile ${wide ? 'ov-tile-wide' : ''}`}>
    <span className="ov-tile-label">{label}</span>
    {value != null && <span className="ov-tile-value">{value}</span>}
    {sub && <span className="ov-tile-sub">{sub}</span>}
    {children}
  </div>
);

export default function ProjectOverview() {
  const [overviews, setOverviews] = useState(null);
  const [canCreate, setCanCreate] = useState(false);
  const [overviewId, setOverviewId] = useState(null);
  const [listError, setListError] = useState('');
  const [loaded, setLoaded] = useState({});            // segKey -> payload
  const [progress, setProgress] = useState({ total: 0, done: 0, failed: [], phase: 'idle' });
  const [filters, setFilters] = useState({ analyses: null, segments: null, severities: [...SEVERITIES], direction: '' });
  const [view, setView] = useState({ unify: false, axis: 'mp', colourBy: 'analysis', sumBy: 'segment', measure: 'sites', showPaths: true,
    rowsSites: 25, rowsClusters: 10, minAnalyses: 2 });
  const [sorts, setSorts] = useState({ summary: { key: 'total', dir: 'desc' }, clusters: { key: 'n', dir: 'desc' },
    sites: { key: 'severity', dir: 'desc' } });
  const [focus, setFocus] = useState(null);
  const [stations, setStations] = useState({});          // `${analysis}|${segKey}` -> {P: rows, N: rows}
  const [stationLoad, setStationLoad] = useState({ total: 0, done: 0 });
  const stationToken = useRef(0);
  const [dialog, setDialog] = useState(null);           // 'new' | 'edit' | 'pdf' | 'criteria'
  const [baseLayer, setBaseLayer] = useState('street');   // the map's base layer: the PDF draws the same one
  const [severity, setSeverity] = useState(() => applySeverityColors(loadSeveritySettings()) && loadSeveritySettings());
  const mapRef = useRef(null);
  const loadToken = useRef(0);

  const loadList = useCallback(async (preferId) => {
    try {
      const data = await fetchOverviews();
      setOverviews(data.overviews || []);
      setCanCreate(Boolean(data.canCreate));
      setListError('');
      const ids = (data.overviews || []).map((o) => o.id);
      const wanted = [preferId, recall()].find((id) => id && ids.includes(id));
      setOverviewId(wanted || ids[0] || null);
    } catch (e) {
      setOverviews([]);
      setListError(e.message);
    }
  }, []);
  useEffect(() => { loadList(); }, [loadList]);

  const overview = useMemo(() => (overviews || []).find((o) => o.id === overviewId) || null, [overviews, overviewId]);
  const projectSignature = overview ? overview.projects.map((p) => p.id).join(',') : '';

  // Load: corridors per project, then one payload per corridor, a few at a time.
  useEffect(() => {
    if (!overview) { setLoaded({}); return undefined; }
    remember(overview.id);
    loadToken.current += 1;
    const token = loadToken.current;
    const live = () => loadToken.current === token;
    setLoaded({});
    setStations({});
    setFocus(null);
    setFilters((f) => ({ ...f, analyses: null, segments: null }));
    setProgress({ total: 0, done: 0, failed: [], phase: 'corridors' });
    (async () => {
      const found = await mapLimit(overview.projects, 3, (p) => fetchCorridors(overview.id, p.id));
      if (!live()) return;
      const corridors = found.flatMap((r) => (r.value ? r.value.corridors : []));
      const failed = found.map((r, i) => (r.error ? `project #${overview.projects[i].id}` : null)).filter(Boolean);
      setProgress({ total: corridors.length, done: 0, failed, phase: 'segments' });
      await mapLimit(corridors, 4, async (c) => {
        try {
          const data = await fetchSegment(overview.id, c.key, c.projectId);
          if (!live()) return;
          setLoaded((prev) => ({ ...prev, [c.key]: data.segment }));
          setProgress((p) => ({ ...p, done: p.done + 1 }));
        } catch (e) {
          if (live()) setProgress((p) => ({ ...p, done: p.done + 1, failed: [...p.failed, c.key.split('|')[1]] }));
        }
      });
      if (live()) setProgress((p) => ({ ...p, phase: 'ready' }));
    })();
    return () => { loadToken.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overview?.id, projectSignature]);

  const segments = useMemo(() => numberSegments(applySeverity(dedupeSegments(Object.values(loaded)), severity))
    .sort((a, b) => a.road.localeCompare(b.road, undefined, { numeric: true }) || ((a.segment || 0) - (b.segment || 0))
      || ((a.mpMin || 0) - (b.mpMin || 0))), [loaded, severity]);
  const available = useMemo(() => ANALYSES.filter((a) => segments.some((s) => (s.analyses || []).includes(a))), [segments]);
  const analyses = useMemo(() => (filters.analyses ? filters.analyses.filter((a) => available.includes(a)) : available),
    [filters.analyses, available]);
  const segKeys = useMemo(() => (filters.segments ? filters.segments.filter((k) => loaded[k]) : segments.map((s) => s.key)),
    [filters.segments, segments, loaded]);
  const shownSegments = useMemo(() => segments.filter((s) => segKeys.includes(s.key)), [segments, segKeys]);
  const roadColors = useMemo(() => Object.fromEntries(
    Array.from(new Set(segments.map((s) => s.road))).map((r, i) => [r, ROAD_COLORS[i % ROAD_COLORS.length]])), [segments]);

  const sites = useMemo(() => (analyses.length
    ? filterSites(segments, { analyses, segments: segKeys, severities: filters.severities, direction: filters.direction })
    : []), [segments, analyses, segKeys, filters.severities, filters.direction]);
  const allClusters = useMemo(() => clusterSites(sites), [sites]);
  const clusters = useMemo(() => allClusters.filter((c) => c.n >= view.minAnalyses), [allClusters, view.minAnalyses]);
  const stats = useMemo(() => overviewStats(shownSegments, sites, allClusters, filters.direction),
    [shownSegments, sites, allClusters, filters.direction]);
  const position = useMemo(() => positionFn(shownSegments, view.axis, view.unify), [shownSegments, view.axis, view.unify]);
  const blocks = useMemo(() => stripBlocks(shownSegments, view.unify), [shownSegments, view.unify]);
  const summaryRows = useMemo(() => summarize(shownSegments, sites, view.sumBy, analyses, filters.direction),
    [shownSegments, sites, view.sumBy, analyses, filters.direction]);

  // ONE analysis selected -> the map shows that analysis the way its report does
  // (station dots, glare bands, slope triangles, sight rays). Loaded per corridor, on demand.
  // Up to three analyses keep their report look together; beyond that the map goes back to coloured runs.
  const details = useMemo(() => (analyses.length >= 1 && analyses.length <= MAX_DETAIL_ANALYSES ? analyses : []), [analyses]);
  const stationAnalyses = useMemo(() => details.filter((a) => DETAIL_ANALYSES.includes(a)), [details]);
  const detail = stationAnalyses.join(',');
  const wantsStations = stationAnalyses.length > 0;
  const shownKeySignature = shownSegments.map((s) => s.key).join(',');
  useEffect(() => {
    if (!overview || !wantsStations) { setStationLoad({ total: 0, done: 0 }); return undefined; }
    stationToken.current += 1;
    const token = stationToken.current;
    const live = () => stationToken.current === token;
    const missing = stationAnalyses.flatMap((a) => shownSegments
      .filter((s) => (s.analyses || []).includes(a) && !stations[`${a}|${s.key}`]).map((s) => ({ a, s })));
    if (!missing.length) { setStationLoad({ total: 0, done: 0 }); return undefined; }
    setStationLoad({ total: missing.length, done: 0 });
    mapLimit(missing, 4, async ({ a, s }) => {
      let detailOf = { directions: {}, reports: {} };
      try { detailOf = (await fetchStations(overview.id, s.key, s.projectId, a)) || detailOf; } catch (e) { /* drawn without detail */ }
      if (!live()) return;
      setStations((prev) => ({ ...prev, [`${a}|${s.key}`]: { directions: detailOf.directions || {}, reports: detailOf.reports || {} } }));
      setStationLoad((p) => ({ ...p, done: p.done + 1 }));
    }).then(() => { if (live()) setStationLoad({ total: 0, done: 0 }); });
    return () => { stationToken.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overview?.id, detail, wantsStations, shownKeySignature]);
  const stationLayers = useMemo(() => {
    if (!wantsStations) return null;
    // one chunk per corridor and direction: each has its own report page
    return stationAnalyses.map((a) => {
      const chunks = [];
      shownSegments.forEach((s) => {
        const held = stations[`${a}|${s.key}`];
        if (!held) return;
        Object.entries(held.directions).forEach(([d, rows]) => {
          if (filters.direction && d !== filters.direction) return;
          chunks.push({ rows, report: held.reports[d] || null, label: `${segmentName(s)} · ${d === 'P' ? 'positive' : 'negative'} direction` });
        });
      });
      return { analysis: a, chunks };
    });
  }, [wantsStations, stationAnalyses, shownSegments, stations, filters.direction]);

  const intersections = useMemo(() => shownSegments.flatMap((s) => (s.intersections || [])
    .map((ix) => ({ ...ix, segKey: s.key, segName: segmentName(s) }))), [shownSegments]);

  const showOnMap = useCallback((item) => {
    if (!item || item.lat == null) return;
    setFocus({ id: item.id, lat: item.lat, lng: item.lng, stamp: Date.now() });
    if (mapRef.current) mapRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  const patchView = (p) => setView((v) => ({ ...v, ...p }));
  const loading = progress.phase === 'corridors' || progress.phase === 'segments';
  const maxBar = Math.max(1, ...stats.byAnalysis.map((b) => b.sites));

  if (overviews === null) return <div className="safety-dashboard ov-dashboard"><div className="safety-placeholder">Loading…</div></div>;

  return (
    <div className="safety-dashboard ov-dashboard"
      style={{ '--sev-severe': severity.colors.severe, '--sev-moderate': severity.colors.moderate, '--sev-minor': severity.colors.minor }}>
      <header className="safety-header">
        <div>
          <h1>Project Overview</h1>
          <p className="safety-muted">Every road of an order on one map, with its deficiencies, clusters and reports.</p>
        </div>
        <div className="ov-header-tools">
          <label className="safety-filter" htmlFor="ov-pick"><span>Overview</span>
            <select id="ov-pick" value={overviewId || ''} onChange={(e) => setOverviewId(Number(e.target.value) || null)}
              disabled={!overviews.length}>
              {!overviews.length && <option value="">No overviews yet</option>}
              {overviews.map((o) => <option key={o.id} value={o.id}>{o.name} ({o.projects.length} project{o.projects.length === 1 ? '' : 's'})</option>)}
            </select>
          </label>
          {overview?.canEdit && <button type="button" className="safety-btn safety-btn-ghost" onClick={() => setDialog('edit')}>Edit</button>}
          {canCreate && <button type="button" className="safety-btn safety-btn-ghost" onClick={() => setDialog('new')}>New overview</button>}
          <button type="button" className="safety-btn safety-btn-ghost" onClick={() => setDialog('criteria')}>Criteria</button>
          <button type="button" className="safety-btn ov-primary" disabled={!overview || loading || !segments.length}
            onClick={() => setDialog('pdf')} title={`Export the ${REPORT_NAME} as a PDF`}>Export PDF report</button>
        </div>
      </header>

      {listError && <div className="safety-error">Could not load the overviews: {listError}</div>}
      {!overviews.length && !listError && (
        <div className="safety-placeholder">
          {canCreate
            ? 'No overview yet. Create one from the RSA projects of an order with "New overview".'
            : 'No overview includes a project you are assigned to yet. An administrator or your Super User can create one.'}
        </div>
      )}

      {overview && (
        <>
          {overview.description && <p className="safety-muted ov-description">{overview.description}</p>}
          {(loading || progress.failed.length > 0) && (
            <div className="safety-note" role="status">
              {progress.phase === 'corridors' && `Finding the analysed roads of ${overview.projects.length} project${overview.projects.length === 1 ? '' : 's'}…`}
              {progress.phase === 'segments' && `Loading ${progress.done} of ${progress.total} segments… The first load of a road reads its analyses and can take a minute; later loads are instant.`}
              {progress.phase === 'ready' && progress.failed.length > 0 && `Could not load: ${progress.failed.join(', ')}.`}
            </div>
          )}
          {stationLoad.total > 0 && (
            <div className="safety-note" role="status">
              Loading the {stationAnalyses.join(', ')} stations: {stationLoad.done} of {stationLoad.total} done.
              The first load reads every station of a road and can take a minute.
            </div>
          )}
          {progress.phase === 'ready' && !segments.length && (
            <div className="safety-placeholder">No analysed road with a surveyed centreline was found in these projects.</div>
          )}

          {segments.length > 0 && (
            <>
              <div className="safety-filterbar">
                <MultiPicker label="Analyses" noun="analyses" options={available.map((a) => ({ key: a, label: `${a} · ${ANALYSIS_NAMES[a]}` }))}
                  selected={analyses} onChange={(next) => setFilters((f) => ({ ...f, analyses: next }))}
                  extraToggle={{ id: 'ov-show-paths', label: 'Show the analysis paths on the map', checked: view.showPaths, onChange: (v) => patchView({ showPaths: v }) }}
                  title="Up to three analyses are drawn with their own report signs; four or more as coloured runs" />
                <MultiPicker label="Roads and segments" noun="segments" wide
                  options={segments.map((s) => ({ key: s.key, label: `${segmentName(s)} · MP ${(s.mpMin || 0).toFixed(1)}–${(s.mpMax || 0).toFixed(1)} · #${s.projectId}` }))}
                  selected={segKeys} onChange={(next) => setFilters((f) => ({ ...f, segments: next }))} />
                <MultiPicker label="Severity" noun="severities" options={SEVERITIES.map((s) => ({ key: s, label: s }))}
                  selected={filters.severities} onChange={(next) => setFilters((f) => ({ ...f, severities: next }))} />
                <label className="safety-filter" htmlFor="ov-dir"><span>Direction</span>
                  <select id="ov-dir" value={filters.direction} onChange={(e) => setFilters((f) => ({ ...f, direction: e.target.value }))}>
                    <option value="">Both</option><option value="P">Positive</option><option value="N">Negative</option>
                  </select>
                </label>
                <div className="safety-filter"><span>Segments</span>
                  <Seg label="Segments" value={view.unify} onChange={(v) => patchView({ unify: v })}
                    options={[[false, 'Separately'], [true, 'Unified per road']]} />
                </div>
                <div className="safety-filter"><span>Position as</span>
                  <Seg label="Position as" value={view.axis} onChange={(v) => patchView({ axis: v })}
                    options={[['mp', 'Milepost'], ['mi', 'Mileage']]} />
                </div>
                <div className="safety-filter"><span>Colour sites by</span>
                  <Seg label="Colour sites by" value={view.colourBy} onChange={(v) => patchView({ colourBy: v })}
                    options={[['analysis', 'Analysis'], ['severity', 'Severity']]} />
                </div>
              </div>

              <div className="ov-main" ref={mapRef}>
                <div className="ov-map-pane">
                  <OverviewMap onBaseLayer={setBaseLayer} segments={shownSegments} sites={sites} clusters={clusters} intersections={intersections} analyses={analyses}
                    colourBy={view.colourBy} roadColors={roadColors} focus={focus} details={details} stationLayers={stationLayers}
                    showPaths={view.showPaths} direction={filters.direction} />
                </div>
                <div className="ov-stats">
                  <Tile label={filters.direction ? `Surveyed · ${filters.direction === 'P' ? 'positive' : 'negative'} direction` : 'Surveyed road'}
                    value={`${stats.miles.toFixed(1)} mi`}
                    sub={`${filters.direction ? '' : `${stats.directionalMi.toFixed(1)} mi counting both directions · `}${stats.segments} segment${stats.segments === 1 ? '' : 's'} · ${stats.roads} road${stats.roads === 1 ? '' : 's'} · ${stats.projects} project${stats.projects === 1 ? '' : 's'}`} />
                  <Tile label="Deficiency sites" value={stats.sites} sub={<><b>{stats.severe} severe</b></>} />
                  <Tile label="Deficient length" value={`${stats.deficientMi.toFixed(1)} mi`} sub={<><b>{stats.pct.toFixed(1)}%</b> of the {stats.directionalMi.toFixed(1)} directional miles analysed</>} />
                  <Tile label="Multi-deficiency clusters" value={stats.clusters} sub={`${stats.clusters3} with 3+ analyses`} />
                  {analyses.includes('ISD') && (
                    <Tile wide label="ISD · intersections analysed" value={stats.intersections}
                      sub={stats.intersections
                        ? <><b>{stats.intersectionsShort} with a deficiency</b> · {stats.maneuvers} maneuver{stats.maneuvers === 1 ? '' : 's'} analysed, {stats.maneuversShort} short of the required sight distance</>
                        : 'No intersection analysis in this selection'} />
                  )}
                  <Tile wide label="Densest road" value={stats.densest ? stats.densest.road : '—'}
                    sub={stats.densest ? <><b>{stats.densest.rate.toFixed(2)} sites per mile</b></> : null} />
                  <Tile wide label={`By analysis · sites · deficient miles${analyses.includes('ISD') ? ' (ISD above)' : ''}`}>
                    <div className="ov-bars">
                      {stats.byAnalysis.filter((b) => analyses.includes(b.analysis)).map((b) => (
                        <div className="ov-bar" key={b.analysis} title={ANALYSIS_NAMES[b.analysis]}>
                          <span>{b.analysis}</span>
                          <span className="ov-bar-track"><span className="ov-bar-fill" style={{ width: `${(b.sites / maxBar) * 100}%`, background: ANALYSIS_COLORS[b.analysis] }} /></span>
                          <span className="ov-bar-n">{b.sites} · {b.miles.toFixed(1)} mi</span>
                        </div>
                      ))}
                      {!stats.byAnalysis.some((b) => analyses.includes(b.analysis)) && <span className="ov-tile-sub">{analyses.length ? 'ISD is reported above' : 'No analysis selected'}</span>}
                    </div>
                  </Tile>
                </div>
              </div>

              <SectionCard title="Milepost strip" meta={`${blocks.length} ${view.unify ? 'road' : 'segment'}${blocks.length === 1 ? '' : 's'} · click a site or an intersection to find it on the map, double-click to open its report`}>
                <OverviewStrips blocks={blocks} sites={sites} clusters={clusters} analyses={analyses}
                  colourBy={view.colourBy} axis={view.axis} onFocus={showOnMap} />
              </SectionCard>

              <SectionCard title="Deficiency summary" meta={`${summaryRows.length} ${view.sumBy === 'road' ? 'road' : 'segment'}${summaryRows.length === 1 ? '' : 's'}`}
                tools={(
                  <>
                    <span className="ov-inline">Rows <Seg label="Summary rows" value={view.sumBy} onChange={(v) => patchView({ sumBy: v })} options={[['road', 'Roads'], ['segment', 'Segments']]} /></span>
                    <span className="ov-inline">Show <Seg label="Summary measure" value={view.measure} onChange={(v) => patchView({ measure: v })} options={[['sites', 'Sites'], ['miles', 'Deficient miles']]} /></span>
                  </>
                )}>
                <SummaryTable rows={summaryRows} analyses={analyses} measure={view.measure} by={view.sumBy}
                  sort={sorts.summary} onSort={(s) => setSorts((x) => ({ ...x, summary: s }))}
                  onRowClick={(r) => showOnMap(r.firstSite)} />
              </SectionCard>

              <SectionCard title="Multi-deficiency clusters"
                meta={`${clusters.length} place${clusters.length === 1 ? '' : 's'} where ${view.minAnalyses}+ analyses coincide`}
                tools={(
                  <>
                    <label className="ov-inline" htmlFor="ov-min-an">At least
                      <select id="ov-min-an" className="ov-select" value={view.minAnalyses} onChange={(e) => patchView({ minAnalyses: Number(e.target.value) })}>
                        <option value={2}>2 analyses</option><option value={3}>3 analyses</option><option value={4}>4 analyses</option>
                      </select>
                    </label>
                    <RowsSelect id="ov-rows-clusters" value={view.rowsClusters} onChange={(n) => patchView({ rowsClusters: n })} />
                  </>
                )}>
                <ClustersTable rows={clusters} position={position} axis={view.axis} unify={view.unify} limit={view.rowsClusters}
                  sort={sorts.clusters} onSort={(s) => setSorts((x) => ({ ...x, clusters: s }))} onRowClick={showOnMap} />
              </SectionCard>

              <SectionCard title="Deficiency sites" meta={`${sites.length} site${sites.length === 1 ? '' : 's'}`}
                tools={<RowsSelect id="ov-rows-sites" value={view.rowsSites} onChange={(n) => patchView({ rowsSites: n })} />}>
                <SitesTable rows={sites} position={position} axis={view.axis} limit={view.rowsSites}
                  sort={sorts.sites} onSort={(s) => setSorts((x) => ({ ...x, sites: s }))} onRowClick={showOnMap} />
              </SectionCard>
            </>
          )}
        </>
      )}

      {(dialog === 'new' || dialog === 'edit') && (
        <ManagerDialog overview={dialog === 'edit' ? overview : null} onClose={() => setDialog(null)}
          onSaved={(o) => { setDialog(null); loadList(o.id); }}
          onDeleted={() => { setDialog(null); setOverviewId(null); loadList(); }} />
      )}
      {dialog === 'criteria' && (
        <CriteriaDialog settings={severity} onClose={() => setDialog(null)}
          onChange={(next) => { const saved = saveSeveritySettings(next); applySeverityColors(saved); setSeverity(saved); }} />
      )}
      {dialog === 'pdf' && overview && (
        <PdfDialog overview={overview} onClose={() => setDialog(null)} basemap={baseLayer}
          counts={{ sites: sites.length, clusters: clusters.length, segments: shownSegments.length }}
          request={{
            filters: { analyses, segments: segKeys, severities: filters.severities, direction: filters.direction },
            options: { axis: view.axis, unify: view.unify, rowsSites: view.rowsSites, rowsClusters: view.rowsClusters,
              minAnalyses: view.minAnalyses, sortSites: sorts.sites, sortClusters: sorts.clusters, severity },
          }} />
      )}
    </div>
  );
}
